/**
 * Typed bags hung on PIXI objects in the room scene.
 *
 * PIXI does not know about these fields; without this module every access needs
 * an untyped cast. Declared once here so a typo in a field name is a compile error.
 *
 * Inventory (grep-verified, not speculative):
 * - furnitureData          RenderFurniture on sprite and marker
 * - markerContainer        PIXI.Container back-link on sprite
 * - __alphaMask            AlphaMask | null (pixel hit-test + mask apply)
 * - __visualBaseOffsetX    number (signed non-mirrored X offset)
 * - __visualOffsetX        number (effective X after mirror)
 * - __visualBottomOffset   number (floor-contact Y offset)
 * - __maxOpaqueWidthPx     number (window footprint width)
 * - __interactionSetup     boolean (pointer handlers already attached)
 */
import type * as PIXI from 'pixi.js';
import type { AlphaMask } from '../../../domain/alphaMask';
import type { RenderFurniture } from '../../../domain/types';

/** Furniture sprite with room-scene custom fields. */
export interface FurnitureSprite extends PIXI.Sprite {
  furnitureData?: RenderFurniture;
  markerContainer?: PIXI.Container;
  __alphaMask?: AlphaMask | null;
  __visualBaseOffsetX?: number;
  __visualOffsetX?: number;
  __visualBottomOffset?: number;
  __maxOpaqueWidthPx?: number;
  __interactionSetup?: boolean;
}

/** Marker container that may carry furnitureData as a fallback bag. */
export interface FurnitureMarker extends PIXI.Container {
  furnitureData?: RenderFurniture;
}

/** Cast once at a boundary when a generic PIXI node is known to be a furniture sprite. */
export function asFurnitureSprite(obj: object | null | undefined): FurnitureSprite | undefined {
  return obj as FurnitureSprite | undefined;
}

/**
 * Cast once at a boundary when a node is known to be a furniture marker.
 * Accepts `object` so domain SceneNode (structurally compatible at runtime) does not
 * need a double `as unknown as PIXI.Container` cast.
 */
export function asFurnitureMarker(obj: object | null | undefined): FurnitureMarker | undefined {
  return obj as FurnitureMarker | undefined;
}

/**
 * Read live furniture data: prefer sprite bag, fall back to marker bag.
 * Same precedence as domain `readFurnitureData`.
 */
export function liveFurnitureData(
  sprite?: FurnitureSprite | null,
  marker?: FurnitureMarker | null,
): RenderFurniture | undefined {
  return sprite?.furnitureData ?? marker?.furnitureData;
}
